import { css } from '@emotion/react';
import { isNullsy } from '~/_shared/utils/typeGuards';
import {
  EXPORT_REPORT_DATA_CURRENT_YEAR, EXPORT_REPORT_DATA_PROJECTED_YEAR,
} from '~/exportReport/exportReport.constants';
import {
  formatReportValue, getReportItemFormatType, getTemplateRowFormatType, isReportDemographicPercentage,
  ReportItemFormatType,
} from '~/exportReport/formatting/exportReportFormatting.helpers';
import {
  ExportReportMapComponent, type ExportReportMapProps,
} from '~/exportReport/map/exportReportMap.component';
import {
  ExportReportSummaryComponent, type ExportReportSummaryProps,
} from '~/exportReport/summary/exportReportSummary.component';
import { ExportReportTableComponent } from '~/exportReport/table/exportReportTable.component';
import {
  ExportReportTableDataRowComponent, type ExportReportTableValue,
} from '~/exportReport/table/exportReportTableDataRow.component';
import { ExportReportTableEmptyRowComponent } from '~/exportReport/table/exportReportTableEmptyRow.component';
import { ExportReportTableHeaderComponent } from '~/exportReport/table/exportReportTableHeader.component';
import { ExportReportTableTitleRowComponent } from '~/exportReport/table/exportReportTableTitleRow.component';
import type {
  ReportTemplate, ReportTemplateDataRow,
} from '~/exportReport/template/reportTemplate';
import {
  getFormulaDemographicIds, isReportFormulaIsTooComplex,
} from '~/exportReport/template/reportTemplate.helpers';
import { getReportFormulaValue } from '~/exportReport/templateRenderer/templateRenderer.helpers';
import type { DemographicsResult } from '~/store/demographics/demographics.repository';

const tableStyle = css({
  marginTop: 10,
});

const titleRowErrorStyle = css({
  color: 'red',
});

const mapsWrapperStyle = css({
  width: '100%',
  height: 300,
  breakInside: 'avoid',
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  marginTop: 10,
});

const tableTitleStyle = css({
  display: 'block',
  fontSize: 18,
  fontWeight: 700,
  textTransform: 'uppercase',
  margin: '20px 0 8px',
});

const pageBreakStyle = css({
  breakAfter: 'page',
});

const REPORT_VALUE_NOT_AVAILABLE = {
  value: 'N/A',
  isError: true,
} as const;

export type ReportTableColumn = Readonly<{
  id: string;
  heading: string;
  secondaryHeading: string;
}>;

type DemographicFormulaValue = Readonly<{
  value: number;
  valueOfTotal: number | null;
}>;

type GetFormulaTableValueArgs = Readonly<{
  columnId: string;
  formula: string;
  templateRow: ReportTemplateDataRow;
  getDemographicData: ExportReportTemplateRendererProps['getDemographicData'];
  getDemographicValue: (columnId: string, demographicId: string) => DemographicFormulaValue | null;
}>;

type GetDemographicTableValueArgs = Readonly<{
  columnId: string;
  templateRow: ReportTemplateDataRow;
  getDemographicData: ExportReportTemplateRendererProps['getDemographicData'];
  getDemographicValue: (columnId: string, demographicId: string) => DemographicFormulaValue | null;
}>;

const getFormulaTableValue = ({
  columnId, formula, templateRow, getDemographicData, getDemographicValue,
}: GetFormulaTableValueArgs): ExportReportTableValue => {
  const formulaDemographicIds = getFormulaDemographicIds(formula);
  const formulaDemographicsData = formulaDemographicIds.map(demographicId => getDemographicData(columnId, demographicId));
  const firstDemographicData = formulaDemographicsData[0];

  if (!firstDemographicData?.success || formulaDemographicsData.some(demographicData => !demographicData?.success)) {
    return REPORT_VALUE_NOT_AVAILABLE;
  }

  const getData = (demographicId: string) => getDemographicValue(columnId, demographicId);
  const value = getReportFormulaValue(formula, getData);

  const unit = templateRow.unit ?? firstDemographicData?.postData ?? '';

  const formatType = formula.endsWith('%')
    ? ReportItemFormatType.PERCENT
    : templateRow.format_type
      ? getTemplateRowFormatType(templateRow.format_type)
      : getReportItemFormatType({ format: firstDemographicData?.agsFormat ?? '', unit, demographicId: templateRow.id });

  return {
    value: formatReportValue({ value, formatType, unit }),
    isError: isNullsy(value),
  };
};

const getDemographicTableValue = ({
  columnId, templateRow, getDemographicData, getDemographicValue,
}: GetDemographicTableValueArgs): ExportReportTableValue => {
  const demographicValue = getDemographicValue(columnId, templateRow.id);
  const demographicData = getDemographicData(columnId, templateRow.id);

  if (!demographicData?.success) {
    return REPORT_VALUE_NOT_AVAILABLE;
  }

  const unit = templateRow.unit ?? demographicData?.postData ?? '';

  const formatType = templateRow.format_type
    ? getTemplateRowFormatType(templateRow.format_type)
    : getReportItemFormatType({ format: demographicData?.agsFormat ?? '', unit, demographicId: templateRow.id });

  const value = (isReportDemographicPercentage(templateRow.id) ? demographicValue?.valueOfTotal : demographicValue?.value) ?? null;
  const formattedValue = formatReportValue({ value, formatType, unit });

  const showAdditionalValue = templateRow.include_percentage_value && demographicValue && demographicValue.valueOfTotal !== null;
  const additionalValue = showAdditionalValue ? formatReportValue({
    value: demographicValue?.valueOfTotal ?? null,
    formatType: ReportItemFormatType.PERCENT,
    unit: '%',
  }) : null;

  return {
    value: formattedValue,
    additionalValue,
  };
};

// TODO: remove after development of PDF Report is finished
const ADD_DEMOGRAPHIC_ID_IN_TITLE = false;

const formatTemplateRowLabel = (label: string): string => label
  .replace('~CY', EXPORT_REPORT_DATA_CURRENT_YEAR.toString())
  .replace('~PY', EXPORT_REPORT_DATA_PROJECTED_YEAR.toString());

type ExportReportTemplateRendererProps = Readonly<{
  template: ReportTemplate;
  tableColumns: ReadonlyArray<ReportTableColumn>;
  summaryProps: ExportReportSummaryProps;
  onMapLoaded?: () => void;
  mapProps: Pick<ExportReportMapProps, 'type' | 'center' | 'paths' | 'css'>;
  getDemographicLabel: (demographicId: string) => string | null;
  getDemographicData: (columnId: string, demographicId: string) => DemographicsResult | null;
  isPreviewMode?: boolean;
}>;

export const ExportReportTemplateRendererComponent = ({
  template, summaryProps, onMapLoaded, mapProps, tableColumns, getDemographicLabel, getDemographicData, isPreviewMode,
}: ExportReportTemplateRendererProps) => {
  const getDemographicValue = (columnId: string, demographicId: string) => {
    const demographicData = getDemographicData(columnId, demographicId);
    if (!demographicData || !demographicData.success) {
      return null;
    }

    return {
      value: demographicData.value,
      valueOfTotal: demographicData.agsValueOfTotal ?? null,
    };
  };

  return (
    <>
      <ExportReportSummaryComponent {...summaryProps} />

      {template.items.map((templateItem, index) => {
        switch (templateItem.type) {
          case 'map': {
            return (
              <div key={`maps-${index}`} css={mapsWrapperStyle}>
                <ExportReportMapComponent
                  onMapLoaded={onMapLoaded}
                  id={`map${index}_roadmap`}
                  mapType="roadmap"
                  {...mapProps}
                />

                <ExportReportMapComponent
                  id={`map${index}_hybrid`}
                  mapType="hybrid"
                  {...mapProps}
                />
              </div>
            );
          }

          case 'page-break': {
            return (
              <div key={`page-break-${index}`} css={pageBreakStyle} />
            );
          }

          case 'line-break': {
            return (
              <div key={`line-break-${index}`}>
                <br />
              </div>
            );
          }

          case 'title': {
            return (
              <div key={`title-${index}`} css={tableTitleStyle}>{templateItem.content}</div>
            );
          }

          case 'table': {
            return (
              <ExportReportTableComponent key={`table-${index}`} css={tableStyle}>
                <ExportReportTableHeaderComponent
                  label={templateItem.title}
                  columnNames={tableColumns}
                />

                <tbody>
                  {templateItem.content.map((templateRow, index) => {
                    if (templateRow.type === 'data-row') {
                      if (templateRow.formula && isReportFormulaIsTooComplex(templateRow.formula)) {
                        return (
                          <ExportReportTableTitleRowComponent
                            css={titleRowErrorStyle}
                            key={templateRow.id}
                            title={`${templateRow.id}: ${templateRow.formula}`}
                          />
                        );
                      }

                      const values = tableColumns.map(column => {
                        if (isPreviewMode) {
                          return {
                            value: templateRow.id,
                          };
                        }

                        if (templateRow.formula) {
                          return getFormulaTableValue({
                            columnId: column.id,
                            formula: templateRow.formula,
                            templateRow,
                            getDemographicData,
                            getDemographicValue,
                          });
                        }
                        else {
                          return getDemographicTableValue({
                            columnId: column.id,
                            templateRow,
                            getDemographicData,
                            getDemographicValue,
                          });
                        }
                      });

                      const demographicLabel = getDemographicLabel(templateRow.id);
                      const isMissingDemographicLabel = !templateRow.formula && !demographicLabel;
                      let regularLabel = templateRow.custom_label ?? demographicLabel ?? (isMissingDemographicLabel ? templateRow.id : '');
                      if (ADD_DEMOGRAPHIC_ID_IN_TITLE) {
                        regularLabel += ` | ${templateRow.id}`;
                      }

                      const label = formatTemplateRowLabel(regularLabel);
                      const isLabelError = isMissingDemographicLabel || values.some(value => value.isError);

                      return (
                        <ExportReportTableDataRowComponent
                          isBold={templateRow.style === 'bold'}
                          isLabelError={isLabelError}
                          key={`data-row${index}`}
                          label={label}
                          values={values}
                        />
                      );
                    }

                    if (templateRow.type === 'title-row') {
                      return (
                        <ExportReportTableTitleRowComponent
                          key={`title-row-${index}`}
                          title={templateRow.content}
                        />
                      );
                    }

                    if (templateRow.type === 'empty-row') {
                      return (
                        <ExportReportTableEmptyRowComponent key={`empty-row-${index}`} />
                      );
                    }

                    return null;
                  })}
                </tbody>
              </ExportReportTableComponent>
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
};
